// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sendMessage from "@store/Framework/Store/Pages/OrderApprovalDetails/Handlers/SendMessage";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import sortBy from "lodash/sortBy";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Based on widget modules\content-library\src\Widgets\RfqQuoteDetails\RfqQuoteDetailsMessages.tsx
export interface OrderApprovalMessage {
    Body: string;
    Username: string;
    CreatedOn: Date;
}

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderApprovalDetails.cartId),
});

const mapDispatchToProps = {
    sendMessage,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCartContext;

export interface OrderApprovalDetailsMessagesStyles {
    messageTextArea?: TextAreaProps;
    sendButton?: ButtonPresentationProps;
    messagesContainer?: GridContainerProps;
    messageGridItem?: GridItemProps;
    userNameText?: TypographyPresentationProps;
    messageDateText?: TypographyPresentationProps;
    messageBodyText?: TypographyPresentationProps;
    messageWritingWrapper?: InjectableCss;
    textAreaNote?: TypographyPresentationProps;
}

export const orderApprovalDetailsMessagesStyles: OrderApprovalDetailsMessagesStyles = {
    messageTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: ${pxToRem(50)};
        `,
    },
    messagesContainer: {
        gap: 20,
        css: css`
            ${GridOffset} {
                margin-bottom: ${pxToRem(20)};
                max-height: ${pxToRem(500)};
                overflow-y: scroll;
            }
            ${GridItemStyle} {
                margin: ${pxToRem(0, 10)};
                padding: ${pxToRem(10, 0)};
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    userNameText: {
        weight: "bold",
    },
    messageBodyText: {
        css: css`
            margin-top: ${pxToRem(10)};

            @media print {
                &&&& {
                    display: block;
                }
            }
        `,
    },
    messageWritingWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            border: 1px solid ${getColor("common.border")};
            padding: ${pxToRem(10, 20)};
            margin-bottom: ${pxToRem(20)};
        `,
    },
    messageDateText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-style: italic;
        `,
    },
    textAreaNote: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-style: italic;
            margin-bottom: ${pxToRem(20)};
            display: block;
        `,
    },
};

const styles = orderApprovalDetailsMessagesStyles;

const OrderApprovalDetailsMessages = ({ sendMessage, cart, cartState }: Props) => {
    const currentOrder = cartState.value || cart;

    const [message, setMessage] = useState("");
    const messageChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.currentTarget.value);
    };

    let messageCollection: OrderApprovalMessage[];

    if (currentOrder && currentOrder.properties.messages) {
        messageCollection = JSON.parse(currentOrder.properties.messages);
    } else {
        messageCollection = [];
    }

    const [sortedMessages, setSortedMessages] = useState<OrderApprovalMessage[]>([]);
    useEffect(() => {
        if (!currentOrder) {
            return;
        }

        setSortedMessages(sortBy(messageCollection || [], ["createdOn"]).reverse());
    }, [currentOrder]);

    const sendClickHandler = () => {
        if (!currentOrder) {
            return;
        }

        sendMessage({ cart, message });
        setMessage("");
    };

    if (!cart) {
        return null;
    }

    return (
        <Accordion headingLevel={2}>
            <AccordionSection title={`${translate("Messages")} (${sortedMessages.length})`}>
                <StyledWrapper {...styles.messageWritingWrapper}>
                    <TextArea
                        {...styles.messageTextArea}
                        label={`${translate("Write a Message")} *`}
                        placeholder={siteMessage("Rfq_GhostMessage") as string}
                        value={message}
                        onChange={messageChangeHandler}
                        maxLength={300}
                    />
                    <Typography {...styles.textAreaNote}>* {translate("Max 500 characters")}</Typography>
                    <Button {...styles.sendButton} disabled={!message.trim()} onClick={sendClickHandler}>
                        {translate("Send Message")}
                    </Button>
                </StyledWrapper>
                <GridContainer {...styles.messagesContainer} data-test-selector="rfqConfirmation_buyerdetails">
                    {sortedMessages.map(message => (
                        <GridItem key={message.CreatedOn.toString()} {...styles.messageGridItem}>
                            <Typography {...styles.userNameText}>{message.Username}</Typography>
                            <Typography {...styles.messageDateText}>
                                <LocalizedDateTime
                                    dateTime={message.CreatedOn}
                                    options={{
                                        year: "numeric",
                                        month: "numeric",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "numeric",
                                    }}
                                />
                            </Typography>
                            <Typography {...styles.messageBodyText}>{message.Body}</Typography>
                        </GridItem>
                    ))}
                </GridContainer>
            </AccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCart(OrderApprovalDetailsMessages)),
    definition: {
        displayName: "Messages",
        allowedContexts: ["OrderApprovalDetailsPage"],
        fieldDefinitions: [],
        group: "Order Approval Details",
    },
};

export default widgetModule;
